// ---------------------------------------------------------------------------
// DeleteWorkspaceDialog — confirmation dialog for irreversible workspace
// deletion. Rendered nested inside the management drawer.
// ---------------------------------------------------------------------------

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Text,
} from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import {
  DataHubClientProvider,
  useDataHubClient,
} from '../../context/DataHubClientContext.js';
import {
  deletePrivateWorkspace,
  PrivateWorkspaceCleanupError,
} from '../../features/private-workspaces/privateWorkspace.js';
import { DataHubService } from '../../services/dataHubService.js';
import { WorkspaceService } from '../../services/workspaceService.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionOutcome,
  EntityType,
  getOperationActionScenario,
  OperationType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';
import type { Workspace } from '../../types/workspace.js';

import { useStyles } from './WorkspaceDrawer.style.js';

export interface DeleteWorkspaceDialogProps {
  workspace: Workspace | null;
  open: boolean;
  onDismiss: () => void;
  onDeleted: (name: string) => void;
}

interface DeleteWorkspaceDialogContentProps extends DeleteWorkspaceDialogProps {
  deleteWorkspace: () => Promise<void>;
}

function DeleteWorkspaceDialogContent({
  workspace,
  open,
  onDismiss,
  onDeleted,
  deleteWorkspace,
}: DeleteWorkspaceDialogContentProps) {
  const styles = useStyles();
  const { recordAction } = useUserBi();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceDeleted, setWorkspaceDeleted] = useState(false);

  const handleDelete = async () => {
    if (workspace === null) return;
    if (workspaceDeleted) {
      onDeleted(workspace.name);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      recordAction({
        panelType: PanelType.WorkspaceDrawer,
        panelRegion: PanelRegion.Overlay,
        actionScenario: getOperationActionScenario(
          EntityType.Workspace,
          OperationType.Delete,
        ),
        actionScenarioType: OperationType.Delete,
        actionGesture: 'click',
        actionOutcome: ActionOutcome.Requested,
      });
      await deleteWorkspace();
      onDeleted(workspace.name);
    } catch (e) {
      if (e instanceof PrivateWorkspaceCleanupError) {
        setWorkspaceDeleted(true);
        setError(
          'The workspace was deleted, but its access dataset could not be cleaned up. Contact support to remove the orphaned dataset.',
        );
      } else {
        setError(e instanceof Error ? e.message : 'Failed to delete workspace');
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete this workspace?</DialogTitle>
          <DialogContent>
            <Text className={styles.dangerBody}>
              This will permanently delete the workspace{' '}
              {workspace?.displayName ?? workspace?.name} ({workspace?.name})
              and everything inside it. This action cannot be undone.
            </Text>

            {error !== null && (
              <MessageBar intent={workspaceDeleted ? 'warning' : 'error'}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onDismiss}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                void handleDelete();
              }}
              disabled={submitting}
            >
              {submitting
                ? 'Deleting…'
                : workspaceDeleted
                  ? 'Close'
                  : 'Delete workspace'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function PrivateWorkspaceDeleteDialog(
  props: DeleteWorkspaceDialogProps & { anchorDatasetId: string },
) {
  const workspaceClient = useHttpClient();
  const dataHubClient = useDataHubClient();
  const dependencies = useMemo(
    () => ({
      workspaceService: new WorkspaceService(workspaceClient),
      dataHubService: new DataHubService(dataHubClient),
    }),
    [dataHubClient, workspaceClient],
  );

  return (
    <DeleteWorkspaceDialogContent
      {...props}
      deleteWorkspace={() =>
        deletePrivateWorkspace(
          props.workspace?.name ?? '',
          props.anchorDatasetId,
          dependencies,
        )
      }
    />
  );
}

export function DeleteWorkspaceDialog(props: DeleteWorkspaceDialogProps) {
  const httpClient = useHttpClient();
  const service = useMemo(() => new WorkspaceService(httpClient), [httpClient]);
  const anchorDatasetId = props.workspace?.accessControl?.aclAnchorDatasetId;

  if (props.open && anchorDatasetId !== undefined) {
    return (
      <DataHubClientProvider>
        <PrivateWorkspaceDeleteDialog
          {...props}
          anchorDatasetId={anchorDatasetId}
        />
      </DataHubClientProvider>
    );
  }

  return (
    <DeleteWorkspaceDialogContent
      {...props}
      deleteWorkspace={() =>
        props.workspace === null
          ? Promise.resolve()
          : service.delete(props.workspace.name)
      }
    />
  );
}
